// agents/src/components/ReassignModal.jsx
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getAllUsers } from '../services/userService';

/**
 * Modal para reasignar una conversación a otro agente
 * @param {boolean} isOpen - Estado del modal (abierto/cerrado)
 * @param {function} onClose - Función para cerrar el modal
 * @param {function} onReassign - Función callback al reasignar (recibe agentId)
 * @param {string} currentAgentId - ID del agente actual (opcional, para no mostrarlo en la lista)
 */
export default function ReassignModal({ isOpen, onClose, onReassign, currentAgentId }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Cargar lista de agentes cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      loadAgents();
    }
  }, [isOpen]);

  const loadAgents = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getAllUsers();
      // Filtrar para mostrar solo agentes (role: 'agent', 'manager', 'admin')
      // y excluir al agente actual
      const filteredAgents = data.users.filter(user => {
        // const isValidRole = ['employee', 'manager', 'admin'].includes(user.role);
        const isNotCurrent = user.id !== currentAgentId;
        return isNotCurrent;
      });
      setAgents(filteredAgents);
    } catch (err) {
      setError(err.message || 'Error al cargar agentes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAgentId) {
      setError('Debes seleccionar un agente');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await onReassign(selectedAgentId);
      onClose();
    } catch (err) {
      setError(err.message || 'Error al reasignar conversación');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Reasignar Conversación</h2>
          <button style={styles.closeBtn} onClick={onClose}>
            ×
          </button>
        </div>

        {error && (
          <div style={styles.errorBox}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={styles.loadingContainer}>
            <p>Cargando agentes...</p>
          </div>
        ) : agents.length === 0 ? (
          <div style={styles.emptyContainer}>
            <p>No hay agentes disponibles para reasignar</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Selecciona un agente:</label>
              <select
                style={styles.select}
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                disabled={submitting}
              >
                <option value="">-- Seleccionar agente --</option>
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name} ({agent.email}) - {agent.role}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.footer}>
              <button
                type="button"
                style={styles.cancelBtn}
                onClick={onClose}
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                style={styles.submitBtn}
                disabled={submitting || !selectedAgentId}
              >
                {submitting ? 'Reasignando...' : 'Reasignar'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: '1px solid #e0e0e0'
  },
  title: {
    margin: 0,
    fontSize: '1.5rem',
    color: '#333'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    color: '#666',
    cursor: 'pointer',
    padding: '0',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'background-color 0.2s'
  },
  errorBox: {
    margin: '1rem 1.5rem',
    padding: '0.75rem',
    backgroundColor: '#fee',
    border: '1px solid #fcc',
    borderRadius: '6px',
    color: '#c00',
    fontSize: '0.9rem'
  },
  loadingContainer: {
    padding: '2rem',
    textAlign: 'center',
    color: '#666'
  },
  emptyContainer: {
    padding: '2rem',
    textAlign: 'center',
    color: '#666'
  },
  formGroup: {
    padding: '1.5rem',
    paddingBottom: '1rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '500',
    color: '#333',
    fontSize: '0.95rem'
  },
  select: {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #ddd',
    borderRadius: '8px',
    fontSize: '1rem',
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    outline: 'none'
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '1rem 1.5rem',
    borderTop: '1px solid #e0e0e0'
  },
  cancelBtn: {
    padding: '0.625rem 1.25rem',
    border: '2px solid #ddd',
    backgroundColor: '#fff',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: '#666'
  },
  submitBtn: {
    padding: '0.625rem 1.25rem',
    border: 'none',
    backgroundColor: '#1A6EFF',
    color: '#fff',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s'
  }
};
